import {
  AlertBox,
  Box,
  Button,
  Checkbox,
  FlexBox,
  FloatingLabel,
  FormError,
  FormField,
  FormInputGroup,
  InputWithFloatingLabel,
  Link,
  StandardForm,
  TextInput,
  Typography,
} from '@vp/swan';
import React, { useContext, useEffect, useState } from 'react';
import { IStartChatData } from '../../interfaces/api-interfaces-miaw';
import { CAContext } from '../../contexts/CAContext';
import { validateEmail, validateName } from '../../utils/validation-utils';
import { getPrivacyPolicyLink } from '../../config/privacy-link';
import { API_ENDPOINTS } from '../../config/api-config';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { MiawChatContext } from '../../contexts/MiawChatContext';

export interface IPrechatMIAWFormProps {
  onSubmit?: (data: IStartChatData) => void;
}

const PrechatFormMIAW: React.FC<IPrechatMIAWFormProps> = ({ onSubmit }) => {
  const {
    chatState: { userInfo, availableSchedule, gcpChatData, chatSession, locale },
  } = useContext(CAContext);

  const {
    state: { isMIAWStartChatError },
  } = useContext(MiawChatContext);

  const [showofflineAlert, setShowOfflineAlert] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showNearOfflineAlert, setShowNearOfflineAlert] = useState(false);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '' });
  const [errors, setErrors] = useState({ firstName: '', lastName: '', email: '' });
  const [touched, setTouched] = useState({ firstName: false, lastName: false, email: false });
  const [isPrivacyPolicyAccepted, setIsPrivacyPolicyAccepted] = useState(false);
  const [bannerText, setBannerText] = useState('');
  const [showBanner, setShowBanner] = useState(false);

  const handleBlur = (field: keyof typeof formData) => {
    setTouched({ ...touched, [field]: true });

    if (field === 'email') {
      setErrors({ ...errors, [field]: validateEmail(formData[field]) });
    } else {
      setErrors({ ...errors, [field]: validateName(formData[field], field === 'firstName' ? 'First name' : 'Last name') });
    }
  };

  const validateForm = (): boolean => {
    const newErrors = {
      firstName: validateName(formData.firstName, 'First name'),
      lastName: validateName(formData.lastName, 'Last name'),
      email: validateEmail(formData.email),
    };

    setErrors(newErrors);
    setTouched({ firstName: true, lastName: true, email: true });

    return !Object.values(newErrors).some((error) => error !== '');
  };

  const handleSubmit = () => {
    if (!showLoading) {
      if (userInfo?.isLoggedIn) {
        onSubmit?.({
          email: userInfo.userData?.email || '',
          firstName: userInfo.userData?.firstName || userInfo.userData?.email || '',
          lastName: userInfo.userData?.lastName || userInfo.userData?.firstName || userInfo.userData?.email || '',
        });
        setShowLoading(true);
        return;
      } else if (validateForm()) {
        onSubmit?.({
          email: formData.email,
          firstName: formData.firstName || formData.email,
          lastName: formData.lastName || formData.firstName || formData.email,
        });
        setShowLoading(true);
      }
    }
  };

  useEffect(() => {
    if (userInfo?.isLoggedIn) {
      setFormData({
        firstName: userInfo.userData?.firstName || '',
        lastName: userInfo.userData?.lastName || '',
        email: userInfo.userData?.email || '',
      });
    }
  }, [userInfo]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.BANNER_CONFIG);

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const data = await response.json();
        const matchedLocaleKey = Object.keys(data).find((key) => key.toLowerCase() === locale);
        if (matchedLocaleKey) {
          const localeConfig = data[matchedLocaleKey];
          if (localeConfig?.showBanner) {
            setBannerText(localeConfig.bannerText);
            setShowBanner(true);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    if (gcpChatData) {
      const { isChatEnabled, remainingTime } = gcpChatData.status;
      // const isChatEnabled = true;
      // const remainingTime = 30;

      if (isChatEnabled && !isNaN(remainingTime) && remainingTime > 0) {
        /** For offline banner, show once remaining time reaches zero */
        setTimeout(() => {
          setShowOfflineAlert(true);
          setShowNearOfflineAlert(false);
        }, remainingTime * 1000);

        /** For near offline banner, show when it reaches 10 mins */
        if (remainingTime > 600) {
          setTimeout(
            () => {
              setShowNearOfflineAlert(true);
            },
            (remainingTime - 600) * 1000,
          );
        } else {
          /** If already less that 10 mins, show banner instantly */
          setShowNearOfflineAlert(true);
        }
      }
    }
  }, [gcpChatData]);

  useEffect(() => {
    if (chatSession === 'miawChatSession') {
      /** Once chat is connected, reset the prechat form */
      setShowLoading(false);
      setIsPrivacyPolicyAccepted(false);
      setFormData({ firstName: '', lastName: '', email: '' });
      setErrors({ firstName: '', lastName: '', email: '' });
      setTouched({ firstName: false, lastName: false, email: false });
    }
  }, [chatSession]);

  useEffect(() => {
    if (isMIAWStartChatError) {
      setShowLoading(false);
    }
  }, [isMIAWStartChatError]);

  return (
    <Box compactMode marginY={9} marginX={5} style={{ overflowY: 'auto' }}>
      <StandardForm marginX={4}>
        {isMIAWStartChatError && <ErrorMessage forcePageRefresh />}
        {userInfo?.isLoggedIn ? (
          <>
            <Typography fontSkin="title-section" mb="3">
              Privacy and Cookie Policy
            </Typography>
            {showBanner && (
              <Box mb="5" p="5">
                <AlertBox skin="info">{bannerText}</AlertBox>
              </Box>
            )}
          </>
        ) : (
          <>
            <Typography fontSkin="title-section" mb="3">
              Hi there!
            </Typography>
            <Typography fontSkin="body-standard" mb="between-subsections">
              Let's start by adding your details.
              <br />
              All fields are required.
            </Typography>
            {showBanner && (
              <Box mb="5">
                <AlertBox skin="info">{bannerText}</AlertBox>
              </Box>
            )}
            <FormField mb="5">
              <FormInputGroup>
                <InputWithFloatingLabel>
                  <FloatingLabel>First name</FloatingLabel>
                  <TextInput
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    onBlur={() => handleBlur('firstName')}
                    placeholder="First name"
                    disabled={showofflineAlert}
                  />
                </InputWithFloatingLabel>{' '}
                {touched.firstName && errors.firstName && <FormError>{errors.firstName}</FormError>}
              </FormInputGroup>
            </FormField>
            <FormField mb="5">
              <FormInputGroup>
                <InputWithFloatingLabel>
                  <FloatingLabel>Last name</FloatingLabel>
                  <TextInput
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    onBlur={() => handleBlur('lastName')}
                    placeholder="Last name"
                    disabled={showofflineAlert}
                  />
                </InputWithFloatingLabel>{' '}
                {touched.lastName && errors.lastName && <FormError>{errors.lastName}</FormError>}{' '}
              </FormInputGroup>
            </FormField>
            <FormField mb="between-actions">
              <FormInputGroup>
                <InputWithFloatingLabel>
                  <FloatingLabel>Email linked to your VistaPrint account</FloatingLabel>
                  <TextInput
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    onBlur={() => handleBlur('email')}
                    placeholder="Email linked to your VistaPrint account"
                    disabled={showofflineAlert}
                  />
                </InputWithFloatingLabel>{' '}
                {touched.email && errors.email && <FormError>{errors.email}</FormError>}
              </FormInputGroup>
            </FormField>
          </>
        )}
        {showofflineAlert && (
          <Box mb="to-actions">
            <AlertBox skin={'warning'}>
              Our specialists are currently offline.
              <br />
              Chat will be available again at {availableSchedule?.weekday},{availableSchedule?.startTime}
            </AlertBox>
          </Box>
        )}
        {showNearOfflineAlert && (
          <Box mb="to-actions">
            <AlertBox skin={'warning'}>
              Our specialists will be signing off soon. The chat will be available again at {availableSchedule?.weekday},{availableSchedule?.startTime}
            </AlertBox>
          </Box>
        )}
        <FlexBox gap="between-icon-and-text" mb="to-actions">
          <Checkbox
            style={{ minWidth: '20px' }}
            checked={isPrivacyPolicyAccepted}
            onChange={() => setIsPrivacyPolicyAccepted(!isPrivacyPolicyAccepted)}
            name="checkbox"
            disabled={showofflineAlert}
          />
          <Typography fontSkin="body-small" textColor={'subtle'}>
            I agree to VistaPrint and its chat service provider recording and using chat communications as described in the{' '}
            <Link href={getPrivacyPolicyLink(locale)}>Privacy and Cookie Policy.</Link>
          </Typography>
        </FlexBox>

        <FlexBox>
          <Button onClick={handleSubmit} skin="primary" disabled={!isPrivacyPolicyAccepted || showofflineAlert} loading={showLoading}>
            Chat now
          </Button>
        </FlexBox>
      </StandardForm>
    </Box>
  );
};

export default PrechatFormMIAW;
